import { motion } from 'framer-motion'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/atoms'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { formStyles } from '@/constants/styles'
import type { AttestationScreenBaseProps } from './AttestationScreenBase'

// Background images from Figma
const imgGroup691314559 = "https://www.figma.com/api/mcp/asset/48828a57-d39f-4c67-bd35-29d933818e50"
const imgMapWorld2 = "https://www.figma.com/api/mcp/asset/09977aa4-6a0b-4011-a140-8c63313b92b8"

export const AttestationScreenMobile = ({
  formData,
  loading,
  error,
  onFormDataChange,
  onSubmit,
}: AttestationScreenBaseProps) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gradient-to-b from-[#f2f1f6] to-[#eae9ec] relative min-h-[922px]"
    >
      {/* Title and Subtitle */}
      <div className="absolute left-1/2 top-[48px] -translate-x-1/2 flex flex-col gap-[8px] items-center text-[#2d2923] text-center w-[378px]">
        <p className="font-['Plus_Jakarta_Sans:Medium',sans-serif] font-medium leading-[48px] text-[36px] tracking-[-0.52px] w-full text-center">
          Account Verification Required
        </p>
        <p className="font-['Plus_Jakarta_Sans:Regular',sans-serif] font-normal leading-[24px] text-[16px] w-full text-center">
          Please complete the following attestation to access your account
        </p>
      </div>

      {/* Main Card */}
      <div className="absolute bg-[rgba(255,255,255,0.32)] border-2 border-solid border-white h-[642px] left-1/2 overflow-clip top-[232px] translate-x-[-50%] w-[378px] rounded-xl">
        {/* Background image */}
        <div className="absolute bottom-[53px] h-[376px] left-[calc(50%+19px)] translate-x-[-50%] w-[1102px]">
          <div className="absolute inset-[-27.66%_-10.53%_-30.32%_-10.53%]">
            <img 
              alt="" 
              className="block max-w-none w-full h-full object-cover" 
              src={imgGroup691314559} 
            />
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="absolute top-[20px] left-1/2 -translate-x-1/2 bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 flex items-center gap-2 rounded z-50 w-[calc(100%-60px)]">
            <AlertCircle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={onSubmit} className="relative z-10">
          {/* Income Verification Section */}
          <div className="absolute left-[32px] top-[32px]">
            <p className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[16px] text-[#2d2923] leading-[24px]">
              Income Verification
            </p>
          </div>

          {/* Income Verification Checkboxes */}
          {/* First checkbox */}
          <div className="absolute left-[32px] top-[76px] w-[314px] flex gap-[12px] items-center">
            <Checkbox
              id="individual_income_test_mobile"
              checked={formData.individual_income_test}
              onCheckedChange={(checked) => {
                onFormDataChange({ individual_income_test: checked as boolean })
              }}
              className={formStyles.checkbox}
            />
            <label htmlFor="individual_income_test_mobile" className="cursor-pointer flex-1">
              <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px] whitespace-pre-wrap">
                Does your annual individual income exceed $200,000?
              </span>
            </label>
          </div>

          {/* Second checkbox */}
          <div className="absolute left-[32px] top-[132px] w-[314px] flex gap-[12px] items-center">
            <Checkbox
              id="joint_income_test_mobile"
              checked={formData.joint_income_test}
              onCheckedChange={(checked) => {
                onFormDataChange({ joint_income_test: checked as boolean })
              }}
              className={formStyles.checkbox}
            />
            <label htmlFor="joint_income_test_mobile" className="cursor-pointer flex-1">
              <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px] whitespace-pre-wrap">
                Does your annual joint household income exceed $300,000?
              </span>
            </label>
          </div>

          {/* Third checkbox */}
          <div className="absolute left-[32px] top-[188px] w-[314px] flex gap-[12px] items-center">
            <Checkbox
              id="net_worth_test_mobile"
              checked={formData.net_worth_test}
              onCheckedChange={(checked) => {
                onFormDataChange({ net_worth_test: checked as boolean })
              }}
              className={formStyles.checkbox}
            />
            <label htmlFor="net_worth_test_mobile" className="cursor-pointer flex-1">
              <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px] whitespace-pre-wrap">
                Does your have a net worth exceeding $1 million (excluding primary residence)?
              </span>
            </label>
          </div>

          {/* Country of Origin */}
          <div className="absolute left-[32px] top-[252px]">
            <label className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px]">
              Country of Origin
            </label>
          </div>
          <div className="absolute left-[32px] top-[284px] w-[314px]">
            <div className="relative">
              <div className="absolute left-[12px] top-1/2 -translate-y-1/2 z-10 size-[20px]">
                <img 
                  alt="" 
                  className="block max-w-none w-full h-full object-cover" 
                  src={imgMapWorld2} 
                />
              </div>
              <Select
                value={formData.country}
                onValueChange={(value) => {
                  onFormDataChange({ country: value })
                }}
              >
                <SelectTrigger className="w-full bg-white border border-[rgba(45,41,35,0.12)] pl-[42px] pr-[18px] py-[14px] h-[48px] text-[14px] text-[rgba(45,41,35,0.72)]">
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="!bg-white text-[#2d2923] border-[rgba(45,41,35,0.12)] shadow-lg">
                  <SelectItem value="United States">United States</SelectItem>
                  <SelectItem value="Canada">Canada</SelectItem>
                  <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                  <SelectItem value="Germany">Germany</SelectItem>
                  <SelectItem value="France">France</SelectItem>
                  <SelectItem value="Switzerland">Switzerland</SelectItem>
                  <SelectItem value="Singapore">Singapore</SelectItem>
                  <SelectItem value="Australia">Australia</SelectItem>
                  <SelectItem value="Japan">Japan</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Market Insights */}
          <div className="absolute left-[32px] top-[360px] flex gap-[12px] items-center">
            <Checkbox
              id="market_insights_opt_in_mobile"
              checked={formData.market_insights_opt_in}
              onCheckedChange={(checked) => {
                onFormDataChange({ market_insights_opt_in: checked as boolean })
              }}
              className={formStyles.checkbox}
            />
            <label htmlFor="market_insights_opt_in_mobile" className="cursor-pointer">
              <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px]">
                Receive monthly market insights
              </span>
            </label>
          </div>

          {/* Affirmation */}
          <div className="absolute left-[32px] top-[396px] w-[314px] flex gap-[12px] items-start">
            <Checkbox
              id="affirmation_mobile"
              checked={formData.affirmation}
              onCheckedChange={(checked) => {
                onFormDataChange({ affirmation: checked as boolean })
              }}
              className={formStyles.checkbox}
            />
            <label htmlFor="affirmation_mobile" className="cursor-pointer flex-1">
              <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] leading-[20px] block whitespace-pre-wrap">
                I affirm that the information provided is true and accurate
              </span>
            </label>
          </div>
          <div className="absolute left-[64px] top-[442px]">
            <p className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[12px] text-[rgba(45,41,35,0.72)] leading-[16px]">
              You must check this box to proceed
            </p>
          </div>

          {/* Submit Button */}
          <div className="absolute left-[32px] top-[482px] w-[314px]">
            <Button
              type="submit"
              variant="primary"
              className="w-full bg-[rgba(45,41,35,0.72)] hover:bg-[rgba(45,41,35,0.85)] text-white font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] leading-[20px] px-4 py-3.5"
              isLoading={loading}
              disabled={!formData.affirmation || !formData.country}
            >
              SUBMIT ATTESTATION
            </Button>
          </div>

          {/* Disclaimer */}
          <p className="absolute font-['Plus_Jakarta_Sans',sans-serif] font-medium leading-[20px] left-[32px] text-[14px] text-[rgba(45,41,35,0.72)] top-[550px] w-[314px] whitespace-pre-wrap">
            This information is required for compliance purposes and to ensure proper access to our platform
          </p>
        </form>
      </div>
    </motion.div>
  )
}
